import PropTypes from 'prop-types';
import { isNull } from 'lodash';
import {
  memo, useState, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import ErrorTooltip from '../../../ErrorTooltip';
import PassengersFilter from '../../FilterBar/PassengersFilter';
import {
  SearchStatus, SearchModes,
} from '../../../../constants';
import {
  tourSearchRequest, resetSearchResults, setTourSearchObjFromQueryString,
  setTripSearchObj,
} from '../../../../store/slices/tripSearch';
import { resetTrips } from '../../../../store/slices/trip';
import setURLParams from '../../../../utils/setURLParams';
import useSearchObjFromQueryString from '../../../../hooks/useSearchObjFromQueryString';
import TourDestinationFilter from '../../../TravelOption/TourInformation/TourFilterBar/TourDestinationFilter';
import TourStartDateFilter from '../../../TravelOption/TourInformation/TourFilterBar/TourDateFilter/TourStartDateFilter';
import TourEndDateFilter from '../../../TravelOption/TourInformation/TourFilterBar/TourDateFilter/TourEndDateFilter';

const TourSearchBar = ({ tripId }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [doOpenTourStartDate, setDoOpenTourStartDate] = useState(false);
  const [doOpenTourEndDate, setDoOpenTourEndDate] = useState(false);
  const initialFormValidations = { startDate: null, endDate: null, destinations: null };
  const [formValidations, setFormValidations] = useState(initialFormValidations);
  const { searchMode: searchModeQuery } = router.query;
  const selectedSearchMode = useSelector((store) => store.tripSearch.selectedSearchMode);
  const { isLoggedIn } = useSelector((store) => store.user);
  const { searchInputs, searchStatus } = useSelector((store) => store.tripSearch);

  const handleOpenFirstDatePicker = (val) => {
    setDoOpenTourStartDate(val);
    if (!val) {
      setTimeout(() => { setDoOpenTourEndDate(true); }, 50);
    }
  };

  useEffect(() => {
    if (doOpenTourStartDate) setDoOpenTourEndDate(false);
  }, [doOpenTourStartDate]);

  const validateSearch = (obj, doShowValidationErrors = true) => {
    const validationObj = { ...initialFormValidations };
    let isValid = true;
    if (!obj) return false;
    if (!obj.startDate) {
      validationObj.checkInDate = 'Start date is required.';
      isValid = false;
    } else if (!obj.endDate) {
      validationObj.checkOutDate = 'End date is required.';
      isValid = false;
    } else if (obj.destinations.length < 1) {
      validationObj.destinations = 'Destination is required';
      isValid = false;
    }
    if (doShowValidationErrors) setFormValidations(validationObj);
    return isValid;
  };

  const handleSearch = (event, doShowValidationErrors = true) => {
    if (event) event.preventDefault();
    if (validateSearch(searchInputs.tour, doShowValidationErrors)) {
      dispatch(resetTrips());
      dispatch(resetSearchResults());
      dispatch(tourSearchRequest({ isTripSearch: true }));
      dispatch(setTripSearchObj({ ...searchInputs.tour }));
      setURLParams({
        searchObj: searchInputs,
        searchedMode: SearchModes.TOUR,
        router,
      });
    }
  };

  const handleResetValidation = () => {
    setFormValidations(initialFormValidations);
  };

  useSearchObjFromQueryString((searchObjFromQueryString) => {
    if (searchStatus !== SearchStatus.INITIAL) return;
    if ((searchModeQuery || SearchModes.CRUISE_TRIP) === selectedSearchMode) {
      dispatch(setTourSearchObjFromQueryString(searchObjFromQueryString));
      if (validateSearch(searchObjFromQueryString, false)) {
        dispatch(resetTrips());
        dispatch(tourSearchRequest({ isTripSearch: true }));
      }
    } else {
      dispatch(resetSearchResults({ isLoggedIn }));
      handleSearch(null, false);
    }
  }, undefined);

  return (
    <form onSubmit={handleSearch} onFocus={handleResetValidation}>
      <div className="mt-2">
        <div className="grid xl:grid-cols-10 grid-cols-12 mt-2">
          <ErrorTooltip
            title={formValidations.destinations || ''}
            open={!isNull(formValidations.destinations)}
            arrow
          >
            <div className="xl:col-span-4 col-span-4">
              <TourDestinationFilter tripId={tripId} />
            </div>
          </ErrorTooltip>
          <ErrorTooltip
            title={formValidations.checkInDate || ''}
            open={!isNull(formValidations.checkInDate)}
            arrow
          >
            <div className="xl:col-span-2 col-span-3">
              <TourStartDateFilter
                doOpenDatePicker={doOpenTourStartDate}
                setDoOpenDatePicker={handleOpenFirstDatePicker}
                tripId={tripId}
              />
            </div>
          </ErrorTooltip>
          <ErrorTooltip
            title={formValidations.checkOutDate || ''}
            open={!isNull(formValidations.checkOutDate)}
            arrow
          >
            <div className="xl:col-span-2 col-span-3">
              <TourEndDateFilter
                doOpenDatePicker={doOpenTourEndDate}
                setDoOpenDatePicker={setDoOpenTourEndDate}
                tripId={tripId}
              />
            </div>
          </ErrorTooltip>
          <div className="col-span-2">
            <button
              type="submit"
              className="rounded-r-2xl bg-searchButton-0 text-searchbtn-0 font-bold text-center w-full h-full"
            >
              Search
            </button>
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap text-white">
          <PassengersFilter />
        </div>
      </div>
    </form>
  );
};

TourSearchBar.propTypes = {
  tripId: PropTypes.string,
};

TourSearchBar.defaultProps = {
  tripId: undefined,
};

export default memo(TourSearchBar);
